import { useTranslation } from 'react-i18next';
import { Arbeitsbereich } from '../../entities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  getHoursAndMinutesFromMinutes,
  getMonthNameByDateObject,
  getWeekdayByDate,
} from '../../../../utils';
import { Suspense, useState } from 'react';
import { useArbeitszeitUpdate } from '../../hooks/useArbeitszeitenUpdate';
import { handleOnCompleted } from './handleOnCompleted';
import {
  ArbeitsbereichSelect,
  ArbeitsbereichSelectLoading,
} from '../Comboboxen';
import { useEditSheetContext } from './EditSheetContext';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { MitarbeiterAvatar } from '../Avatar/MitarbeiterAvatar';
import { faArrowLeft, faXmarkLarge } from '@fortawesome/pro-regular-svg-icons';
import { Button, SheetClose, SheetHeader, SheetTitle } from '@e2n/ui';

// eslint-disable-next-line max-lines-per-function
export function Header() {
  const { t } = useTranslation();
  const { arbeitszeit } = useEditSheetContext();
  const arbeitszeitDate = arbeitszeit.datum ?? new Date();
  const date = `${
    getWeekdayByDate(arbeitszeitDate).shortage
  }., ${arbeitszeitDate.getDate()}. ${getMonthNameByDateObject(
    arbeitszeitDate,
  )}`;
  const [arbeitszeitUpdate] = useArbeitszeitUpdate();
  const [isUpdating, setIsUpdating] = useState(false);
  if (!arbeitszeit || !arbeitszeit.mitarbeiter) {
    return null;
  }

  const { hours, minutes } = arbeitszeit
    ? getHoursAndMinutesFromMinutes(arbeitszeit.dauer)
    : { hours: 0, minutes: 0 };

  async function onChangeArbeitsbereich(newArbeitsbereich: Arbeitsbereich) {
    setIsUpdating(true);
    arbeitszeitUpdate({
      arbeitszeitUpdateInput: {
        arbeitsbereichId: newArbeitsbereich.id,
      },
      arbeitszeitId: arbeitszeit.id,
    })
      .then((result) => {
        if (!result.data) {
          setIsUpdating(false);
          return;
        }
        handleOnCompleted(result.data, t);
        setIsUpdating(false);
      })
      // Hier werden die Fehler abgefangen, die nicht von den Union Type gehandelt werden konnten
      .catch((e) => {
        toast({
          variant: 'destructive',
          title: t(
            'domain.arbeitszeiten.sheet.header.arbeitszeitUpdateUnnownError',
          ),
        });
        Sentry.captureException(e);
        setIsUpdating(false);
      });
  }

  return (
    <SheetHeader className="bg-neutrals-200">
      <SheetTitle>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between px-4 pt-2">
            <div className="w-full flex justify-end gap-2 md:hidden">
              <SheetClose>
                <Button
                  variant="ghost"
                  className="text-neutrals-800"
                  size="icon">
                  <FontAwesomeIcon icon={faXmarkLarge} />
                </Button>
              </SheetClose>
            </div>
            <SheetClose className="hidden md:flex md:items-center md:gap-2">
              <FontAwesomeIcon className="cursor-pointer" icon={faArrowLeft} />
              <span className="text-sm text-neutrals-800">
                {t('domain.arbeitszeiten.sheet.header.bearbeiten')}
              </span>
            </SheetClose>
            <span className="hidden md:inline-block md:text-sm md:text-neutrals-800 md:text-right">
              {t('domain.arbeitszeiten.sheet.header.letzteAenderungAm', {
                date: arbeitszeit.geaendertAm?.toLocaleString('de', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                }),
              })}
              <br />
              <span>
                {t('domain.arbeitszeiten.sheet.header.letzteAenderungVon', {
                  user: arbeitszeit.geaendertVon,
                })}
              </span>
            </span>
          </div>
          <hr className="border-dashed border-neutrals-400" />
          <div className="px-4 pb-2 flex gap-1 items-center md:items-stretch justify-between text-sm font-normal divide-x divide-neutrals-400">
            <div className="flex items-center px-2 grow gap-2 max-w-52">
              <MitarbeiterAvatar
                vorname={arbeitszeit.mitarbeiter.vorname}
                nachname={arbeitszeit.mitarbeiter.nachname}
                bildUrl={arbeitszeit.mitarbeiter.bildUrl}
                extern={arbeitszeit.mitarbeiter.extern}
                className="bg-neutrals-100"
              />
              <span className="truncate font-semibold">
                {`${arbeitszeit.mitarbeiter.vorname} ${arbeitszeit.mitarbeiter.nachname}`}
              </span>
            </div>
            <div className="hidden md:px-2 md:flex md:items-center md:grow">
              {date}
            </div>
            <div className="w-auto px-2 md:w-52">
              <Suspense
                fallback={
                  <ArbeitsbereichSelectLoading
                    name={arbeitszeit.arbeitsbereich.name}
                  />
                }>
                <ArbeitsbereichSelect
                  defaultArbeitsbereich={arbeitszeit.arbeitsbereich}
                  onChange={onChangeArbeitsbereich}
                  isUpdating={isUpdating}
                />
              </Suspense>
            </div>
            <div className="hidden md:flex md:items-center md:grow">
              <div className="flex gap-1 items-baseline grow px-2">
                <span className="font-semibold">{hours}</span>
                <span className=" text-neutrals-700 text-xs">
                  {t('general.date.hours')}
                </span>
                <span className="font-semibold">{minutes}</span>
                <span className=" text-neutrals-700 text-xs">
                  {t('general.date.minutes')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </SheetTitle>
    </SheetHeader>
  );
}
